'use client'

import { useMemo, useState } from 'react'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useFilteredData } from '@/hooks/useFilters'
import { isBillable } from '@/lib/calculations'
import { periodDateRangeLabel } from '@/lib/dates'
import type { Task } from '@/types'

const MAX_PROJECT_ROWS = 12

// REVEAL: Janko Mataja's hours under "[Internal] Remangu AWS - TIPO initiatives"
// (almost entirely "Periodic technical reviews"), broken out as its own bucket
// rather than folded into the generic Internal number.
const REVEAL_PROJECT = '[Internal] Remangu AWS - TIPO initiatives'
const REVEAL_ENGINEER = 'Janko Mataja'

export default function KpisPage() {
  const { isLoading, tasks, odooUrl } = useDashboardData()
  const { periodTS, filterParams } = useFilteredData()
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())

  const toggleProject = (proj: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev)
      if (next.has(proj)) next.delete(proj)
      else next.add(proj)
      return next
    })
  }

  const taskById = useMemo(() => {
    const map: Record<number, Task> = {}
    tasks.forEach(t => { map[t.id] = t })
    return map
  }, [tasks])

  const periodLabel = useMemo(
    () => periodDateRangeLabel(filterParams.customDateFrom, filterParams.customDateTo, filterParams.period),
    [filterParams],
  )

  // Billable / REVEAL / Internal split of this period's total hours — three
  // mutually exclusive buckets that add up to 100%.
  const hourSplit = useMemo(() => {
    const total = periodTS.reduce((a, ts) => a + ts.hours, 0)
    const billable = periodTS.filter(isBillable).reduce((a, ts) => a + ts.hours, 0)
    const reveal = periodTS
      .filter(ts => ts.proj === REVEAL_PROJECT && ts.user === REVEAL_ENGINEER)
      .reduce((a, ts) => a + ts.hours, 0)
    const internal = Math.max(total - billable - reveal, 0)
    return {
      total,
      billablePct: total > 0 ? (billable / total) * 100 : 0,
      revealPct: total > 0 ? (reveal / total) * 100 : 0,
      internalPct: total > 0 ? (internal / total) * 100 : 0,
    }
  }, [periodTS])

  // Each project's share of total hours logged this period — colored by whether it
  // counts as billable (see isBillable), so the utilization number above has a visual
  // breakdown of exactly what's driving it.
  const projectShares = useMemo(() => {
    const map: Record<string, { hours: number; billable: boolean }> = {}
    let total = 0
    periodTS.forEach(ts => {
      total += ts.hours
      const entry = (map[ts.proj] ||= { hours: 0, billable: isBillable(ts) })
      entry.hours += ts.hours
    })
    return Object.entries(map)
      .map(([proj, v]) => ({ proj, hours: v.hours, billable: v.billable, pct: total > 0 ? (v.hours / total) * 100 : 0 }))
      .sort((a, b) => b.hours - a.hours)
  }, [periodTS])

  const shownProjects = projectShares.slice(0, MAX_PROJECT_ROWS)
  const restProjects = projectShares.slice(MAX_PROJECT_ROWS)
  const restHours = restProjects.reduce((a, p) => a + p.hours, 0)

  // Which task, logged by which engineer, within each project — the drill-down
  // behind a project's total once you expand it.
  const taskBreakdownByProject = useMemo(() => {
    const byProj: Record<string, Record<string, { taskId: number; taskName: string; user: string; hours: number }>> = {}
    periodTS.forEach(ts => {
      const byKey = (byProj[ts.proj] ||= {})
      const key = `${ts.task_id}::${ts.user}`
      const entry = (byKey[key] ||= { taskId: ts.task_id, taskName: ts.task_name || '(logged directly, no task)', user: ts.user, hours: 0 })
      entry.hours += ts.hours
    })
    const result: Record<string, Array<{ taskId: number; taskName: string; user: string; hours: number }>> = {}
    Object.entries(byProj).forEach(([proj, byKey]) => {
      result[proj] = Object.values(byKey).sort((a, b) => b.hours - a.hours)
    })
    return result
  }, [periodTS])

  if (isLoading) return <div className="text-slate-400 text-center py-16">Loading...</div>

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
        <h3 className="text-base font-semibold mb-4">KPIs</h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 font-[family-name:var(--font-display)]">Billable Utilization</div>
            <div className={`text-2xl font-bold font-[family-name:var(--font-display)] ${hourSplit.billablePct > 60 ? 'text-emerald-600' : hourSplit.billablePct > 40 ? 'text-amber-600' : 'text-red-600'}`}>
              {hourSplit.billablePct.toFixed(0)}%
            </div>
            <p className="text-xs text-slate-400 mt-1">Billable (MSO/PSO/Customers) hours / total hours, {periodLabel}</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 font-[family-name:var(--font-display)]">REVEAL</div>
            <div className="text-2xl font-bold font-[family-name:var(--font-display)] text-blue-600">
              {hourSplit.revealPct.toFixed(0)}%
            </div>
            <p className="text-xs text-slate-400 mt-1">{REVEAL_ENGINEER}&apos;s hours on {REVEAL_PROJECT} / total hours, {periodLabel}</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 font-[family-name:var(--font-display)]">Internal</div>
            <div className="text-2xl font-bold font-[family-name:var(--font-display)] text-slate-500">
              {hourSplit.internalPct.toFixed(0)}%
            </div>
            <p className="text-xs text-slate-400 mt-1">Everything else (not billable, not REVEAL) / total hours, {periodLabel}</p>
          </div>
        </div>

        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 font-[family-name:var(--font-display)]">
          Hours by Project <span className="normal-case">({periodLabel})</span>
        </div>
        <div className="space-y-2">
          {shownProjects.map(p => {
            const expanded = expandedProjects.has(p.proj)
            const breakdown = taskBreakdownByProject[p.proj] || []
            return (
              <div key={p.proj}>
                <button type="button" onClick={() => toggleProject(p.proj)} className="w-full text-left group">
                  <div className="flex items-center justify-between text-sm mb-0.5">
                    <span className="flex items-center gap-1 truncate max-w-[75%] group-hover:text-blue-600 dark:group-hover:text-blue-400">
                      {expanded ? <ChevronDown size={13} className="shrink-0 text-slate-400" /> : <ChevronRight size={13} className="shrink-0 text-slate-400" />}
                      <span className="truncate" title={p.proj}>{p.proj}</span>
                    </span>
                    <span className="text-slate-400 shrink-0 ml-2">{p.pct.toFixed(0)}% · {p.hours.toFixed(0)}h</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${p.billable ? 'bg-emerald-500' : 'bg-slate-400'}`}
                      style={{ width: `${Math.min(p.pct, 100)}%` }}
                    />
                  </div>
                </button>
                {expanded && (
                  <div className="mt-1.5 ml-4 pl-3 border-l border-slate-200 dark:border-slate-800 space-y-1">
                    {breakdown.length === 0 ? (
                      <p className="text-xs text-slate-400 py-1">No hours in this period.</p>
                    ) : (
                      <div className="max-h-[32rem] overflow-y-auto pr-1 space-y-1">
                        {breakdown.map(row => {
                          const task = taskById[row.taskId]
                          return (
                            <div key={`${row.taskId}-${row.user}`} className="flex items-center gap-2 text-xs">
                              {task ? (
                                <a href={`${odooUrl}/odoo/project.project/${task.projId}/tasks/${task.id}`} target="_blank" rel="noopener noreferrer"
                                  className="flex-1 truncate text-slate-700 dark:text-slate-300 hover:underline" title={row.taskName}>
                                  {row.taskName}
                                </a>
                              ) : (
                                <span className="flex-1 truncate text-slate-500" title={row.taskName}>{row.taskName}</span>
                              )}
                              <span className="text-slate-300 dark:text-slate-600 shrink-0">#{row.taskId}</span>
                              <span className="text-slate-400 shrink-0">{row.user}</span>
                              <span className="text-slate-400 shrink-0 w-12 text-right">{row.hours.toFixed(1)}h</span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {restProjects.length > 0 && (
          <p className="text-xs text-slate-400 mt-2">+{restProjects.length} more projects, {restHours.toFixed(0)}h combined</p>
        )}
        <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />Billable</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-400 inline-block" />Non-billable</span>
        </div>
      </section>
    </div>
  )
}
